import { ArrowRight, Sparkles } from 'lucide-react';
import { heroStats } from '../mock';
import { TopoLines, LayeredMountains, CircuitAccent, CompassAccent } from './graphics/Decor';

const COMPASS_ACCENT_SIZE_PX = 56;

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const HeroBackdrop = () => (
  <>
    <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
    <div className="absolute inset-0 topo-bg pointer-events-none" />
    <TopoLines className="absolute top-16 left-0 w-full h-72 pointer-events-none" opacity={0.4} />
    <div className="absolute -top-20 -right-16 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#8DA9C4]/40 to-transparent blur-3xl pointer-events-none animate-float" />
    <div className="absolute top-1/3 -left-24 w-[360px] h-[360px] rounded-full bg-gradient-to-tr from-[#B8C7D6]/45 to-transparent blur-3xl pointer-events-none" style={{ animation: 'floatSlow 11s ease-in-out infinite' }} />
    <CircuitAccent className="hidden md:block absolute top-24 right-8 w-64 opacity-70 pointer-events-none" />
    <div className="hidden md:block absolute bottom-40 left-8 pointer-events-none">
      <CompassAccent size={COMPASS_ACCENT_SIZE_PX} />
    </div>
    <LayeredMountains className="absolute bottom-0 left-0 right-0 w-full h-64 md:h-80 pointer-events-none" />
  </>
);

const StatCard = ({ stat, index }) => (
  <div
    className="rounded-2xl border border-slate-200/80 bg-white/70 backdrop-blur-sm px-3 py-3 sm:px-5 sm:py-4 hover:shadow-md hover:border-slate-300 transition-all animate-fade-up"
    style={{ animationDelay: `${index * 80 + 200}ms` }}
    data-testid={`hero-stat-card-${index}`}
  >
    <div
      className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0B2545] tracking-tight"
      data-testid={`hero-stat-value-${index}`}
    >
      {stat.value}
    </div>
    <div
      className="mt-1 text-[10.5px] sm:text-[12px] uppercase tracking-[0.14em] font-medium text-slate-500"
      data-testid={`hero-stat-label-${index}`}
    >
      {stat.label}
    </div>
  </div>
);

const Hero = () => (
  <section id="top" className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
    <HeroBackdrop />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-8 animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-300/70 bg-white/70 backdrop-blur-sm px-3 py-1.5 text-[12px] font-medium text-slate-600 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#3E5C76]" />
            Washington State Title Industry, Reinvented
          </div>
          <h1 className="mt-6 font-serif text-[38px] leading-[1.05] sm:text-[56px] md:text-[70px] lg:text-[76px] font-medium tracking-[-0.02em] text-[#0B2545]">
            Modern Tools.
            <br />
            <span className="text-gradient-navy">Washington Title.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[16.5px] md:text-[18px] leading-relaxed text-slate-600">
            Northwest Title Alliance builds and licenses AI-powered products designed
            specifically for title professionals across Washington State — combining deep
            industry expertise with modern technology to reshape how the work gets done.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              onClick={() => scrollTo('#contact')}
              className="group inline-flex items-center gap-2 rounded-full bg-[#0B2545] hover:bg-[#13315C] text-white text-[14.5px] font-medium px-6 py-3.5 transition-all shadow-md hover:shadow-lg"
              data-testid="hero-request-info-btn"
            >
              Request Information
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollTo('#products')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-[#0B2545] text-[14.5px] font-medium px-6 py-3.5 transition-colors"
              data-testid="hero-explore-products-btn"
            >
              Explore Products
            </button>
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-3 lg:gap-4 lg:items-end">
          {heroStats.map((s, i) => (
            <StatCard key={s.label} stat={s} index={i} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
